/* istanbul ignore file */
// libs
import { Routes, useParams } from 'react-router-dom';
// components
import OpportunityContext from 'domain/sales/opportunity/OpportunityContext';
import { Page404 } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import { ProtectedRouter } from 'infrastructure/routing';
import { Navigate, Route } from 'framework/routing';
import { GlobalProtectedRoute } from 'framework/authorization';
import OpportunityViewPage from 'domain/sales/opportunity/show';
import OpportunityTimelinePage from 'domain/sales/opportunity/Timeline';
import OpportunitiesListPage from 'domain/sales/opportunity/list';
import { CreateOpportunity, UpdateOpportunity } from 'domain/sales/opportunity/createupdate';
// services
import { CREATE_OPPORTUNITY, READ_OPPORTUNITIES_LIST, READ_TIMELINE } from 'domain/sales/salesService';
import { READ_OPPORTUNITY } from './opportunityService';
import ShowOpportunityContext from 'domain/sales/opportunity/show/ShowOpportunityContext';
import { OpportunityDocumentStorageContext } from './shared/storage/context/OpportunityDocumentStorageContext';
import { OPPORTUNITY_PATH } from 'domain/shared/core/sharedRouteConstants';
import { useOpportunityRoute, useOpportunityUrl } from './opportunityRouting';

const OpportunityRouter = () => {
  const {
    createRoute: createEntityRoute,
    updateRoute,
    listRoute,
    singleEntityRoute,
    timelineRoute,
    presaleTimelineRoute,
  } = useOpportunityRoute();
  const { isAuthenticated } = useAuthenticationService();

  return (
    <ProtectedRouter basePath={OPPORTUNITY_PATH}>
      <Routes>
        <Route
          path={listRoute}
          element={
            <GlobalProtectedRoute globalPermissionToCheck={READ_OPPORTUNITIES_LIST}>
              <OpportunitiesListPage />
            </GlobalProtectedRoute>
          }
        />

        <Route
          path={createEntityRoute}
          element={
            <GlobalProtectedRoute globalPermissionToCheck={CREATE_OPPORTUNITY}>
              <OpportunityDocumentStorageContext>
                <CreateOpportunity />
              </OpportunityDocumentStorageContext>
            </GlobalProtectedRoute>
          }
        />

        <Route
          path={singleEntityRoute}
          element={
            <OpportunityContext permissionToCheck={READ_OPPORTUNITY}>
              <OpportunityDocumentStorageContext>
                <OpportunityViewPage />
              </OpportunityDocumentStorageContext>
            </OpportunityContext>
          }
        />

        <Route
          path={updateRoute}
          element={
            <OpportunityContext permissionToCheck="UPDATE">
              <OpportunityDocumentStorageContext>
                <UpdateOpportunity />
              </OpportunityDocumentStorageContext>
            </OpportunityContext>
          }
        />

        <Route
          path={timelineRoute}
          element={
            <OpportunityContext permissionToCheck={READ_TIMELINE}>
              <ShowOpportunityContext>
                <OpportunityDocumentStorageContext>
                  <OpportunityTimelinePage />
                </OpportunityDocumentStorageContext>
              </ShowOpportunityContext>
            </OpportunityContext>
          }
        />
        <Route path={presaleTimelineRoute} element={<RedirectToTimeline />} />

        <Route path="*" element={<Page404 isAuthenticated={isAuthenticated} />} />
      </Routes>
    </ProtectedRouter>
  );
};

const RedirectToTimeline = () => {
  const { getTimelineUrl } = useOpportunityUrl();
  const { id: oppId, timelineEntryId } = useParams<Dictionary<string>>();
  const baseUrl = getTimelineUrl(oppId);
  const url = timelineEntryId ? `${baseUrl}/${timelineEntryId}` : baseUrl;

  return <Navigate to={url} replace />;
};

OpportunityRouter.getRouterPath = () => '/opportunities/*';

export default OpportunityRouter;
